import { useOverlayState } from "@heroui/react";
import type { WeeklyScheduleEntry } from "@upsquad/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUser } from "@/src/auth/store/auth-selectors";
import { Button, Modal, Spinner, Tabs } from "@/src/common/components/ui";
import { useAppSelector } from "@/src/common/redux/store";
import { toastService } from "@/src/common/services/toast.service";
import { getActiveCommunityId } from "@/src/community/store/community-selectors";
import {
	useAddUnavailableDateMutation,
	useDeleteUnavailableDateMutation,
	useGetAvailabilityQuery,
	useSetWeeklyScheduleMutation,
} from "../services/member-http.service";
import { UnavailableDatesTab } from "./availability-tabs/unavailable-dates-tab";
import { WeeklyScheduleTab } from "./availability-tabs/weekly-schedule-tab";

interface AvailabilityModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export function AvailabilityModal({ isOpen, onClose }: AvailabilityModalProps) {
	const { t } = useTranslation("member");
	const user = useAppSelector(getUser);
	const communityId = useAppSelector(getActiveCommunityId) ?? "";
	const state = useOverlayState({ isOpen, onOpenChange: (open) => !open && onClose() });

	const { data: availability, isLoading } = useGetAvailabilityQuery(
		{ communityId, userId: user?.id ?? "" },
		{ skip: !communityId || !user?.id || !isOpen },
	);

	const [setWeeklySchedule, { isLoading: isSaving }] = useSetWeeklyScheduleMutation();
	const [addUnavailableDate] = useAddUnavailableDateMutation();
	const [deleteUnavailableDate] = useDeleteUnavailableDateMutation();

	const [schedule, setSchedule] = useState<WeeklyScheduleEntry[]>([]);

	useEffect(() => {
		if (availability) {
			const grouped = new Map<number, WeeklyScheduleEntry>();
			for (const slot of availability.weekly_schedule) {
				const existing = grouped.get(slot.day_of_week);
				if (existing) {
					existing.slots.push({ start_time: slot.start_time, end_time: slot.end_time });
				} else {
					grouped.set(slot.day_of_week, {
						day_of_week: slot.day_of_week,
						slots: [{ start_time: slot.start_time, end_time: slot.end_time }],
					});
				}
			}
			setSchedule(Array.from(grouped.values()));
		}
	}, [availability]);

	const handleSave = async () => {
		try {
			await setWeeklySchedule({ communityId, payload: { schedule } }).unwrap();
			toastService.success(t("availability.save_success"));
			onClose();
		} catch {
			toastService.error(t("availability.save_error"));
		}
	};

	const handleAddDate = async (date: string) => {
		try {
			await addUnavailableDate({ communityId, payload: { date } }).unwrap();
		} catch {
			toastService.error(t("availability.date_add_error"));
		}
	};

	const handleDeleteDate = async (dateId: string) => {
		try {
			await deleteUnavailableDate({ communityId, dateId }).unwrap();
		} catch {
			toastService.error(t("availability.date_delete_error"));
		}
	};

	return (
		<Modal state={state}>
			<Modal.Backdrop>
				<Modal.Container size="lg">
					<Modal.Dialog>
						<Modal.Header>
							<h2 className="text-lg font-semibold">{t("availability.title")}</h2>
						</Modal.Header>
						<Modal.Body className="min-h-[400px]">
							{isLoading ? (
								<div className="flex justify-center py-8">
									<Spinner size="lg" />
								</div>
							) : (
								<Tabs>
									<Tabs.ListContainer>
										<Tabs.List aria-label={t("availability.title")}>
											<Tabs.Tab id="weekly">
												{t("availability.tab_weekly")}
												<Tabs.Indicator />
											</Tabs.Tab>
											<Tabs.Tab id="unavailable">
												{t("availability.tab_unavailable")}
												<Tabs.Indicator />
											</Tabs.Tab>
										</Tabs.List>
									</Tabs.ListContainer>
									<Tabs.Panel id="weekly" className="pt-4">
										<WeeklyScheduleTab schedule={schedule} onChange={setSchedule} />
									</Tabs.Panel>
									<Tabs.Panel id="unavailable" className="pt-4">
										<UnavailableDatesTab dates={availability?.unavailable_dates ?? []} onAdd={handleAddDate} onDelete={handleDeleteDate} />
									</Tabs.Panel>
								</Tabs>
							)}
						</Modal.Body>
						<Modal.Footer className="flex justify-end gap-2">
							<Button variant="ghost" onPress={onClose}>
								{t("profile.cancel")}
							</Button>
							<Button variant="primary" isPending={isSaving} onPress={handleSave}>
								{({ isPending }) => (
									<>
										{isPending ? <Spinner color="current" size="sm" /> : null}
										{isPending ? t("profile.saving") : t("profile.save")}
									</>
								)}
							</Button>
						</Modal.Footer>
					</Modal.Dialog>
				</Modal.Container>
			</Modal.Backdrop>
		</Modal>
	);
}
